/**
 * StudioChat — RIGHT sidebar agent chat.
 *
 * Header has a session picker + "new" button. Body is a scrolling message
 * list with streaming thinking + tool pills surfaced inline. Footer is a
 * textarea + send button — Enter sends, Shift+Enter newlines.
 */

import { useEffect, useRef, useState } from 'react'
import { MessageSquarePlus, Sparkles, Trash2, Loader2 } from 'lucide-react'
import { useStudioStore } from '@/stores/useStudioStore'
import { StudioChatMessage } from './StudioChatMessage'

export function StudioChat() {
  const sessions = useStudioStore((s) => s.sessions)
  const activeSessionId = useStudioStore((s) => s.activeSessionId)
  const messages = useStudioStore((s) => s.messages)
  const isStreaming = useStudioStore((s) => s.isStreaming)
  const streamingThinking = useStudioStore((s) => s.streamingThinking)
  const streamingToolCalls = useStudioStore((s) => s.streamingToolCalls)
  const activeBlockId = useStudioStore((s) => s.activeBlockId)
  const activeProjectId = useStudioStore((s) => s.activeProjectId)
  const activeUniverseId = useStudioStore((s) => s.activeUniverseId)

  const newSession = useStudioStore((s) => s.newSession)
  const loadSession = useStudioStore((s) => s.loadSession)
  const deleteSession = useStudioStore((s) => s.deleteSession)
  const sendChat = useStudioStore((s) => s.sendChat)

  const [input, setInput] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showSessionPicker, setShowSessionPicker] = useState(false)

  // Auto-scroll on new message or streaming thinking growth
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, streamingThinking, isStreaming])

  // Auto-resize textarea
  useEffect(() => {
    if (!textareaRef.current) return
    textareaRef.current.style.height = 'auto'
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
  }, [input])

  const onSubmit = async () => {
    const text = input.trim()
    if (!text || isStreaming) return
    setInput('')
    await sendChat(text)
  }

  // Context badge: tell the user (briefly) what the agent has anchored
  const contextLabel = activeBlockId
    ? 'this block'
    : activeProjectId
      ? 'this project'
      : activeUniverseId
        ? 'this universe'
        : 'all of studio'

  const activeSession = sessions.find((s) => s.session_id === activeSessionId)

  return (
    <div className="h-full flex flex-col">
      {/* Header: session picker + new */}
      <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between flex-shrink-0">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSessionPicker((v) => !v)}
            className="text-sm font-light text-neutral-200 hover:text-neutral-100 flex items-center gap-1"
          >
            <Sparkles className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.5} />
            <span className="truncate max-w-[180px]">
              {activeSession?.name || 'Studio Chat'}
            </span>
          </button>
          {showSessionPicker && (
            <div className="absolute top-full mt-1 left-0 z-20 w-72 max-h-80 overflow-y-auto bg-neutral-900 border border-neutral-700 rounded-md shadow-lg">
              {sessions.length === 0 && (
                <div className="px-3 py-2 text-xs text-neutral-500">No sessions yet.</div>
              )}
              {sessions.map((s) => (
                <div
                  key={s.session_id}
                  className={
                    'flex items-center gap-2 px-3 py-2 text-xs hover:bg-neutral-800 cursor-pointer ' +
                    (s.session_id === activeSessionId ? 'bg-neutral-800/60 text-neutral-100' : 'text-neutral-300')
                  }
                  onClick={() => {
                    void loadSession(s.session_id)
                    setShowSessionPicker(false)
                  }}
                >
                  <span className="truncate flex-1">{s.name || 'Studio'}</span>
                  <span className="text-neutral-500 text-[10px]">{s.total_turns}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      void deleteSession(s.session_id)
                    }}
                    className="text-neutral-500 hover:text-red-400"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            void newSession()
            setShowSessionPicker(false)
          }}
          className="w-8 h-8 rounded hover:bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-neutral-100"
          title="New conversation"
        >
          <MessageSquarePlus className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Context badge */}
      <div className="px-4 py-1.5 border-b border-neutral-800/50 text-[10px] uppercase tracking-wide text-neutral-500 flex-shrink-0">
        anchored to {contextLabel}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {messages.length === 0 && !isStreaming && (
          <div className="text-xs text-neutral-500 font-light leading-relaxed">
            Start the conversation. The agent can read your universes,
            projects, and blocks, and can create, update, score, archive, or
            restore them on your behalf.
          </div>
        )}
        {messages.map((m) => (
          <StudioChatMessage key={m.id} msg={m} />
        ))}

        {/* Streaming: thinking + in-flight tool calls + spinner */}
        {isStreaming && (
          <div className="mb-4">
            {streamingThinking && (
              <div className="mb-2 px-3 py-2 bg-neutral-900/50 border-l-2 border-neutral-700 text-[11px] text-neutral-400 whitespace-pre-wrap leading-relaxed font-light">
                {streamingThinking}
              </div>
            )}
            {streamingToolCalls.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {streamingToolCalls.map((tc, i) => (
                  <div
                    key={i}
                    className={
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border font-light ' +
                      (tc.ok === true
                        ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300'
                        : tc.ok === false
                          ? 'bg-red-950/40 border-red-800/50 text-red-300'
                          : 'bg-neutral-800/40 border-neutral-700 text-neutral-400')
                    }
                  >
                    {tc.ok === null && <Loader2 className="w-3 h-3 animate-spin" />}
                    <span>{tc.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>working…</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-neutral-800 px-3 py-3 flex-shrink-0">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void onSubmit()
            }
          }}
          placeholder="Say something to the agent…"
          rows={1}
          disabled={isStreaming}
          className="w-full bg-neutral-800 border border-neutral-700 rounded-md px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-500 resize-none focus:outline-none focus:border-neutral-500 disabled:opacity-50"
          style={{ minHeight: 38, maxHeight: 200 }}
        />
        <div className="text-[10px] text-neutral-600 mt-1.5 text-right font-light">
          Enter to send · Shift+Enter for newline
        </div>
      </div>
    </div>
  )
}
